import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';

import { bannerPlacements, type BannerPlacement } from '@/features/banners/api';
import { BannersPage } from '@/features/banners/BannersPage';

/** Размещение по умолчанию — слайдер: с него начинается главная и с него же начинают работу. */
const DEFAULT_PLACEMENT: BannerPlacement = 'HERO';

export interface BannersSearch {
  placement?: BannerPlacement;
  /** Открытая форма: `new` — создание, идентификатор — правка. */
  edit?: string;
}

/**
 * Состояние раздела живёт в адресе: ссылку на баннер пересылают коллеге, и открыться она
 * должна тем же экраном — с нужным размещением и открытой формой.
 */
export const Route = createFileRoute('/_app/banners')({
  validateSearch: (search: Record<string, unknown>): BannersSearch => ({
    placement: bannerPlacements.find((value) => value === search.placement && value !== DEFAULT_PLACEMENT),
    edit: typeof search.edit === 'string' && search.edit !== '' ? search.edit : undefined,
  }),
  component: BannersRoute,
});

function BannersRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const onPlacementChange = useCallback(
    (placement: BannerPlacement) => {
      void navigate({
        search: (previous) => ({
          ...previous,
          placement: placement === DEFAULT_PLACEMENT ? undefined : placement,
          // Форма закрывается вместе со сменой размещения: она заведена под другие требования
          // к картинкам, и оставить её открытой значило бы сохранить баннер не туда.
          edit: undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const onEditingChange = useCallback(
    (edit: string | null) => {
      void navigate({
        search: (previous) => ({ ...previous, edit: edit ?? undefined }),
        // Открытие и закрытие формы — шаг истории: «назад» из формы возвращает к списку,
        // как и ожидается от бокового экрана, открытого по ссылке.
        replace: false,
      });
    },
    [navigate],
  );

  return (
    <BannersPage
      placement={search.placement ?? DEFAULT_PLACEMENT}
      editing={search.edit ?? null}
      onPlacementChange={onPlacementChange}
      onEditingChange={onEditingChange}
    />
  );
}
